import React from 'react';
import { useForm } from 'react-hook-form';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser, faPhone, faEnvelope, faMapMarkerAlt, faUserPlus, faCheckCircle, faCity, faLock } from "@fortawesome/free-solid-svg-icons";
import { sendApiRequest } from "../utils";
import Swal from 'sweetalert2';
import { useNavigate } from "react-router-dom";

const CustomerRegistration = () => {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm();

  const navigate = useNavigate();

  const onSubmit = async (data) => {
    try {
      const response = await sendApiRequest('api/customers/register', data, 'POST');
      
      if (response.data.success) {
        Swal.fire({
          title: 'Registration Successful!',
          html: `
            <div style="text-align: center; line-height: 1.8;">
              <p><strong>Name:</strong> ${response?.data.data.customer.customerName}</p>
              <p><strong>Email:</strong> ${response.data.data.customer.customerEmail}</p>
              <p><strong>Phone:</strong> ${response.data.data.customer.customerPhone}</p>
            </div>
          `,
          icon: 'success',
          confirmButtonText: 'Continue',
          confirmButtonColor: '#8F8431',
          background: '#F9F9F6'
        }).then(() => {
          navigate('/');
        });
      } else {
        Swal.fire({
          title: 'Registration Failed',
          text: response.data.message || 'Error registering customer',
          icon: 'error',
          confirmButtonText: 'Try Again',
          confirmButtonColor: '#8F8431',
          background: '#F9F9F6'
        });
      }
    } catch (error) {
      Swal.fire({
        title: 'Error',
        text: error.response?.data?.message || 'Something went wrong',
        icon: 'error',
        confirmButtonText: 'Try Again',
        confirmButtonColor: '#8F8431',
        background: '#F9F9F6'
      });
    }
  };

  const states = [
    "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
    "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand",
    "Karnataka", "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur",
    "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Punjab",
    "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura",
    "Uttar Pradesh", "Uttarakhand", "West Bengal"
  ];

  return (
    <div className="min-h-screen bg-[#EDE9E0] py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Neomorphic Main Card */}
        <div className="bg-[#EDE9E0] rounded-3xl p-8 md:p-12 shadow-[12px_12px_28px_#d4c9b8,-12px_-12px_28px_#ffffff] border border-[#E5E0D0]">
          
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-[#2C3A2A]">Customer Registration</h2>
            <p className="text-[#5F6A5A] mt-3">Join the Dharaa Pariwar Family</p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Customer Name */}
            <div>
              <label className="block text-sm font-medium text-[#3C4A3A] mb-2 ml-1">Customer Name</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <FontAwesomeIcon icon={faUser} className="text-[#8F8431] text-xl" />
                </div>
                <input
                  type="text"
                  placeholder="Enter full name"
                  {...register("customerName", { required: "Customer name is required" })}
                  className="w-full pl-12 pr-5 py-4 rounded-2xl bg-[#EDE9E0] shadow-[inset_6px_6px_12px_#d4c9b8,inset_-6px_-6px_12px_#ffffff] focus:shadow-[inset_8px_8px_16px_#d4c9b8,inset_-8px_-8px_16px_#ffffff] outline-none text-[#2C3A2A]"
                />
              </div>
              {errors.customerName && <p className="text-red-600 text-sm mt-1.5 ml-1">{errors.customerName.message}</p>}
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm font-medium text-[#3C4A3A] mb-2 ml-1">Phone Number</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <FontAwesomeIcon icon={faPhone} className="text-[#8F8431] text-xl" />
                </div>
                <input
                  type="tel"
                  placeholder="Enter phone number"
                  {...register("customerPhone", { 
                    required: "Phone number is required",
                    pattern: { value: /^\d{10}$/, message: "10-digit phone number required" }
                  })}
                  className="w-full pl-12 pr-5 py-4 rounded-2xl bg-[#EDE9E0] shadow-[inset_6px_6px_12px_#d4c9b8,inset_-6px_-6px_12px_#ffffff] focus:shadow-[inset_8px_8px_16px_#d4c9b8,inset_-8px_-8px_16px_#ffffff] outline-none text-[#2C3A2A]"
                />
              </div>
              {errors.customerPhone && <p className="text-red-600 text-sm mt-1.5 ml-1">{errors.customerPhone.message}</p>}
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-[#3C4A3A] mb-2 ml-1">Email Address</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <FontAwesomeIcon icon={faEnvelope} className="text-[#8F8431] text-xl" />
                </div>
                <input
                  type="email"
                  placeholder="Enter email address"
                  {...register("customerEmail", { 
                    required: "Email is required",
                    pattern: { value: /^\S+@\S+$/i, message: "Invalid email address" }
                  })}
                  className="w-full pl-12 pr-5 py-4 rounded-2xl bg-[#EDE9E0] shadow-[inset_6px_6px_12px_#d4c9b8,inset_-6px_-6px_12px_#ffffff] focus:shadow-[inset_8px_8px_16px_#d4c9b8,inset_-8px_-8px_16px_#ffffff] outline-none text-[#2C3A2A]"
                />
              </div>
              {errors.customerEmail && <p className="text-red-600 text-sm mt-1.5 ml-1">{errors.customerEmail.message}</p>}
            </div>

            {/* Complete Address */}
            <div>
              <label className="block text-sm font-medium text-[#3C4A3A] mb-2 ml-1">Complete Address</label>
              <div className="relative">
                <div className="absolute top-4 left-4 pointer-events-none">
                  <FontAwesomeIcon icon={faMapMarkerAlt} className="text-[#8F8431] text-xl" />
                </div>
                <textarea
                  placeholder="Enter complete address"
                  {...register("customerAddress", { required: "Address is required" })}
                  className="w-full pl-12 pr-5 py-4 rounded-2xl bg-[#EDE9E0] shadow-[inset_6px_6px_12px_#d4c9b8,inset_-6px_-6px_12px_#ffffff] focus:shadow-[inset_8px_8px_16px_#d4c9b8,inset_-8px_-8px_16px_#ffffff] outline-none text-[#2C3A2A] min-h-[110px] resize-y"
                />
              </div>
              {errors.customerAddress && <p className="text-red-600 text-sm mt-1.5 ml-1">{errors.customerAddress.message}</p>}
            </div>

            {/* City */}
            <div>
              <label className="block text-sm font-medium text-[#3C4A3A] mb-2 ml-1">City</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <FontAwesomeIcon icon={faCity} className="text-[#8F8431] text-xl" />
                </div>
                <input
                  type="text"
                  placeholder="Enter city"
                  {...register("city", { required: "City is required" })}
                  className="w-full pl-12 pr-5 py-4 rounded-2xl bg-[#EDE9E0] shadow-[inset_6px_6px_12px_#d4c9b8,inset_-6px_-6px_12px_#ffffff] focus:shadow-[inset_8px_8px_16px_#d4c9b8,inset_-8px_-8px_16px_#ffffff] outline-none text-[#2C3A2A]"
                />
              </div>
              {errors.city && <p className="text-red-600 text-sm mt-1.5 ml-1">{errors.city.message}</p>}
            </div>

            {/* State */}
            <div>
              <label className="block text-sm font-medium text-[#3C4A3A] mb-2 ml-1">State</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <FontAwesomeIcon icon={faMapMarkerAlt} className="text-[#8F8431] text-xl" />
                </div>
                <select
                  {...register("state", { required: "State is required" })}
                  className="w-full pl-12 pr-10 py-4 rounded-2xl bg-[#EDE9E0] shadow-[inset_6px_6px_12px_#d4c9b8,inset_-6px_-6px_12px_#ffffff] focus:shadow-[inset_8px_8px_16px_#d4c9b8,inset_-8px_-8px_16px_#ffffff] outline-none text-[#2C3A2A] appearance-none"
                >
                  <option value="">Select State</option>
                  {states.map((state, index) => (
                    <option key={index} value={state}>{state}</option>
                  ))}
                </select>
              </div>
              {errors.state && <p className="text-red-600 text-sm mt-1.5 ml-1">{errors.state.message}</p>}
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-[#3C4A3A] mb-2 ml-1">Password</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <FontAwesomeIcon icon={faLock} className="text-[#8F8431] text-xl" />
                </div>
                <input
                  type="password"
                  placeholder="Create password"
                  {...register("password", { 
                    required: "Password is required",
                    minLength: { value: 6, message: "Minimum 6 characters" }
                  })}
                  className="w-full pl-12 pr-5 py-4 rounded-2xl bg-[#EDE9E0] shadow-[inset_6px_6px_12px_#d4c9b8,inset_-6px_-6px_12px_#ffffff] focus:shadow-[inset_8px_8px_16px_#d4c9b8,inset_-8px_-8px_16px_#ffffff] outline-none text-[#2C3A2A]"
                />
              </div>
              {errors.password && <p className="text-red-600 text-sm mt-1.5 ml-1">{errors.password.message}</p>}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full mt-8 py-4 rounded-2xl font-semibold text-lg bg-[#8F8431] text-white shadow-[6px_6px_14px_#d4c9b8,-6px_-6px_14px_#ffffff] active:shadow-[inset_4px_4px_10px_#7A7130,inset_-4px_-4px_10px_#A89A5E] transition-all flex items-center justify-center gap-3 disabled:opacity-70"
            >
              {isSubmitting ? (
                <>Processing...</>
              ) : (
                <>
                  <FontAwesomeIcon icon={faUserPlus} />
                  Register Customer
                </>
              )}
            </button>
          </form>

          <div className="mt-8 text-center text-sm text-[#5F6A5A]">
            By registering, you agree to our{" "}
            <span className="text-[#8F8431] hover:underline cursor-pointer">Terms of Service</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerRegistration;